import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { http } from "../lib/axios";
import * as agreementsApi from "../lib/apiHostingAgreements";
import {
  groupAgreementsByStatus,
  mergeAgreementUpdate,
  normalizeAgreement,
  normalizeAgreementList,
  removeAgreementById,
  replaceArtworkInList,
} from "../lib/hostingAgreementViewModel";

function getApiFn(name) {
  return agreementsApi?.[name];
}

async function getCurrentArtistProfile() {
  const meRes = await http.get("/api/accounts/me/");
  const userId = meRes?.data?.id;
  if (!userId) return null;

  const artistRes = await http.get(`/artists/?user=${userId}`);
  const artist = Array.isArray(artistRes?.data) ? artistRes.data[0] : null;
  return artist || null;
}

async function listArtistAgreementsFallback() {
  const artist = await getCurrentArtistProfile();
  const response = await agreementsApi.listMyArtworkAgreements();
  const normalized = normalizeAgreementList(response).agreements;

  if (!artist?.id) {
    return {
      agreements: normalized,
      artist,
      raw: response,
    };
  }

  const agreements = normalized.filter((item) => Number(item?.artist?.id) === Number(artist.id));
  return {
    agreements,
    artist,
    raw: response,
  };
}

async function listArtistAgreements() {
  const listArtistArtworkAgreements = getApiFn("listArtistArtworkAgreements");
  if (typeof listArtistArtworkAgreements === "function") {
    return listArtistArtworkAgreements();
  }
  return listArtistAgreementsFallback();
}

async function fetchArtistArtworks(artistId) {
  if (!artistId) return [];
  const response = await http.get(`/artworks/?artist=${artistId}&view=card`);
  return Array.isArray(response?.data) ? response.data : [];
}

function extractAgreementFromPayload(payload) {
  return payload?.agreement || payload?.data?.agreement || payload?.data || payload;
}

function getRelatedUpdates(payload) {
  return payload?.related_updates || payload?.data?.related_updates || {};
}

export function useArtistHostedArtworks({
  enabled = true,
  onError,
} = {}) {
  const [agreements, setAgreementsState] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  const groupedAgreements = useMemo(
    () => groupAgreementsByStatus(agreements),
    [agreements]
  );

  const setAgreements = (updater) => {
    setAgreementsState((prev) => (
      typeof updater === "function" ? updater(prev) : updater
    ));
  };

  const applyAgreementUpdate = (updatedAgreement) => {
    setAgreementsState((prev) => mergeAgreementUpdate(prev, updatedAgreement));
  };

  const applyMutationPayload = (payload) => {
    const agreement = extractAgreementFromPayload(payload);
    const relatedUpdates = getRelatedUpdates(payload);

    if (agreement?.id) {
      applyAgreementUpdate(agreement);
    }

    if (relatedUpdates?.original_agreement?.id) {
      applyAgreementUpdate(relatedUpdates.original_agreement);
    }

    if (relatedUpdates?.replacement_agreement?.id) {
      applyAgreementUpdate(relatedUpdates.replacement_agreement);
    }

    if (relatedUpdates?.artwork?.id) {
      setArtworks((prev) => replaceArtworkInList(prev, relatedUpdates.artwork));
    }
  };

  const refreshAgreements = useCallback(async () => {
    if (!enabled) return [];

    const isInitialLoad = !hasLoadedRef.current;
    setError(null);
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const result = await listArtistAgreements();
      const normalized = Array.isArray(result?.agreements)
        ? result.agreements.map((item) => normalizeAgreement(item?.raw || item))
        : normalizeAgreementList(result?.raw || result).agreements;

      setAgreementsState(normalized);
      hasLoadedRef.current = true;

      const artistId = result?.artist?.id || normalized[0]?.artist?.id || null;
      if (artistId) {
        const nextArtworks = await fetchArtistArtworks(artistId);
        setArtworks(nextArtworks);
      }

      return normalized;
    } catch (err) {
      setError(err);
      if (typeof onError === "function") onError(err);
      throw err;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [enabled, onError]);

  useEffect(() => {
    if (!enabled) return;
    refreshAgreements().catch(() => {});
  }, [enabled, refreshAgreements]);

  const openAgreementById = (agreementId) => (
    agreements.find((agreement) => Number(agreement?.id) === Number(agreementId)) || null
  );

  const acceptAgreement = async (agreementId) => {
    const payload = await agreementsApi.acceptArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const rejectAgreement = async (agreementId) => {
    const payload = await agreementsApi.rejectArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const counterofferAgreement = async (agreementId, patchTerms, options = {}) => {
    const mode = options?.mode || "respond";
    let payload;

    if (mode === "changeTerms") {
      payload = await agreementsApi.createReplacementHostOffer(
        options?.artworkId,
        options?.originalAgreementId || agreementId,
        patchTerms,
        { hostId: options?.hostId }
      );
    } else {
      payload = await agreementsApi.counterofferArtworkAgreement(agreementId, patchTerms);
    }

    applyMutationPayload(payload);
    return payload;
  };

  const requestAgreementCancellation = async (agreementId) => {
    const payload = await agreementsApi.requestCancelArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const confirmAgreementCancellation = async (agreementId) => {
    const payload = await agreementsApi.confirmCancelArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const rejectAgreementCancellation = async (agreementId) => {
    const payload = await agreementsApi.rejectCancelArtworkAgreement(agreementId);
    applyMutationPayload(payload);
    return payload;
  };

  const markAgreementSold = async (agreementId, options = {}) => {
    let payload;
    const markArtworkAgreementSold = getApiFn("markArtworkAgreementSold");
    const markHostedAgreementSold = getApiFn("markHostedAgreementSold");

    if (typeof markArtworkAgreementSold === "function") {
      payload = await markArtworkAgreementSold(agreementId, options);
    } else {
      const agreement = openAgreementById(agreementId);
      const artworkId = options?.artworkId || agreement?.artwork?.id;
      if (!artworkId) {
        throw new Error("markAgreementSold requires an agreementId with an associated artwork.");
      }
      await http.patch(`/artworks/${artworkId}/mark_sold/`);
      if (typeof markHostedAgreementSold === "function") {
        await markHostedAgreementSold(artworkId);
      }
      payload = {
        agreement: {
          ...(agreement?.raw || agreement),
          id: agreementId,
          status: "SOLD",
        },
        related_updates: {
          artwork: {
            id: artworkId,
            status: "sold",
          },
        },
      };
    }

    applyMutationPayload(payload);
    return payload;
  };

  return {
    agreements,
    groupedAgreements,
    artworks,
    loading,
    refreshing,
    error,

    refreshAgreements,
    setAgreements,

    openAgreementById,
    applyAgreementUpdate,
    applyMutationPayload,

    acceptAgreement,
    rejectAgreement,
    counterofferAgreement,
    requestAgreementCancellation,
    confirmAgreementCancellation,
    rejectAgreementCancellation,
    markAgreementSold,
  };
}

export function removeArtistAgreement(agreements, agreementId) {
  return removeAgreementById(agreements, agreementId);
}
